import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { applicationsAPI, tasksAPI, profilesAPI, favoritesAPI, mediaAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  FileText, 
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Calendar,
  MapPin,
  ChevronDown,
  ChevronUp,
  Trash2,
  ListTodo,
  Send,
  HourglassIcon,
  CheckCheck,
  Copy,
  Mail,
  Image as ImageIcon,
  X,
  Check,
  FileDown,
  Pencil
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import { HelpButton } from '../components/HelpButton';
import { ApplicationExportDialog } from '../components/applications/ApplicationExportDialog';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: HourglassIcon },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  waitlisted: { label: 'Waitlisted', color: 'bg-blue-100 text-blue-700', icon: Clock },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const TASK_CATEGORIES = [
  { value: 'application', label: 'Application' },
  { value: 'preparation', label: 'Preparation' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'setup', label: 'Setup' },
  { value: 'followup', label: 'Follow-up' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'text-gray-500' },
  medium: { label: 'Medium', color: 'text-blue-600' },
  high: { label: 'High', color: 'text-orange-600' },
  urgent: { label: 'Urgent', color: 'text-red-600' },
};

export const Applications = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [savedEvents, setSavedEvents] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedApp, setExpandedApp] = useState(null);
  const [tasks, setTasks] = useState({});
  
  // Dialog states
  const [isNewAppOpen, setIsNewAppOpen] = useState(false);
  const [isNewTaskOpen, setIsNewTaskOpen] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [selectedAppForAction, setSelectedAppForAction] = useState(null);
  
  const [newAppData, setNewAppData] = useState({
    event_id: '',
    profile_id: '',
    task_template_id: '',
    notes: '',
    image_ids: [],
  });
  
  const [copyData, setCopyData] = useState({
    target_event_id: '',
    copy_images: true,
    copy_notes: true,
    copy_tasks: false,
  });
  
  const [emailData, setEmailData] = useState({
    recipient_email: '',
    subject: '',
    message: '',
    include_images: true,
  });
  
  const [editAppData, setEditAppData] = useState({
    profile_id: '',
    notes: '',
    image_ids: [],
    coordinator_email: '',
  });
  
  const [newTaskData, setNewTaskData] = useState({
    title: '',
    category: 'other',
    priority: 'medium',
    due_date: '',
    notes: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [appsRes, profilesRes, templatesRes, eventsRes, mediaRes] = await Promise.all([
        applicationsAPI.getAll(),
        profilesAPI.getAll(),
        tasksAPI.getTemplates(),
        favoritesAPI.getAll(),
        mediaAPI.getAll(),
      ]);
      setApplications(appsRes.data);
      setProfiles(profilesRes.data);
      setTemplates(templatesRes.data);
      setSavedEvents(eventsRes.data);
      setMediaItems(mediaRes.data || []);
      
      // Fetch tasks for all applications to show progress bars
      const allApps = appsRes.data;
      if (allApps.length > 0) {
        const taskPromises = allApps.map(app => 
          tasksAPI.getAll({ application_id: app.id }).catch(() => ({ data: [] }))
        );
        const taskResults = await Promise.all(taskPromises);
        const tasksMap = {};
        allApps.forEach((app, index) => {
          tasksMap[app.id] = taskResults[index].data;
        });
        setTasks(tasksMap);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasksForApp = async (appId) => {
    try {
      const response = await tasksAPI.getAll({ application_id: appId });
      setTasks(prev => ({ ...prev, [appId]: response.data }));
    } catch (error) {
      toast.error('Failed to load tasks');
    }
  };

  const toggleExpanded = async (appId) => {
    if (expandedApp === appId) {
      setExpandedApp(null);
    } else {
      setExpandedApp(appId);
      if (!tasks[appId]) {
        await fetchTasksForApp(appId);
      }
    }
  };

  const handleCreateApplication = async (e) => {
    e.preventDefault();
    
    if (!newAppData.event_id) {
      toast.error('Please select an event');
      return;
    }

    try {
      await applicationsAPI.create({
        event_id: newAppData.event_id,
        profile_id: newAppData.profile_id && newAppData.profile_id !== 'none' ? newAppData.profile_id : null,
        task_template_id: newAppData.task_template_id && newAppData.task_template_id !== 'none' ? newAppData.task_template_id : null,
        notes: newAppData.notes || null,
        image_ids: newAppData.image_ids || [],
      });
      toast.success('Application created');
      setIsNewAppOpen(false);
      setNewAppData({ event_id: '', profile_id: '', task_template_id: '', notes: '', image_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create application');
    }
  };

  // Copy application to another event
  const handleCopyApplication = async (e) => {
    e.preventDefault();
    
    if (!copyData.target_event_id) {
      toast.error('Please select a target event');
      return;
    }

    try {
      await applicationsAPI.copy({
        source_application_id: selectedAppForAction.id,
        target_event_id: copyData.target_event_id,
        copy_images: copyData.copy_images,
        copy_notes: copyData.copy_notes,
        copy_tasks: copyData.copy_tasks,
      });
      toast.success('Application copied successfully');
      setIsCopyDialogOpen(false);
      setCopyData({ target_event_id: '', copy_images: true, copy_notes: true, copy_tasks: false });
      setSelectedAppForAction(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy application');
    }
  };

  // Email application to coordinator
  const handleEmailApplication = async (e) => {
    e.preventDefault();
    
    if (!emailData.recipient_email) {
      toast.error('Please enter recipient email');
      return;
    }

    try {
      const response = await applicationsAPI.email({
        application_id: selectedAppForAction.id,
        recipient_email: emailData.recipient_email,
        subject: emailData.subject || null,
        message: emailData.message || null,
        include_images: emailData.include_images,
      });
      
      if (response.data.demo) {
        toast.success('Demo mode: Email would be sent (configure RESEND_API_KEY for live emails)');
      } else {
        toast.success(`Application emailed to ${emailData.recipient_email}`);
      }
      
      setIsEmailDialogOpen(false);
      setEmailData({ recipient_email: '', subject: '', message: '', include_images: true });
      setSelectedAppForAction(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  // Update application images
  const handleUpdateImages = async (appId, imageIds) => {
    try {
      await applicationsAPI.update(appId, { image_ids: imageIds });
      toast.success('Images updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update images');
    }
  };

  // Open copy dialog
  const openCopyDialog = (app) => {
    setSelectedAppForAction(app);
    setCopyData({ target_event_id: '', copy_images: true, copy_notes: true, copy_tasks: false });
    setIsCopyDialogOpen(true);
  };

  // Open email dialog
  const openEmailDialog = (app) => {
    setSelectedAppForAction(app);
    setEmailData({ 
      recipient_email: app.coordinator_email || '', 
      subject: '', 
      message: '', 
      include_images: true 
    });
    setIsEmailDialogOpen(true);
  };

  // Open export dialog
  const openExportDialog = (app) => {
    setSelectedAppForAction(app);
    setIsExportDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (app) => {
    setSelectedAppForAction(app);
    setEditAppData({
      profile_id: app.profile_id || '',
      notes: app.notes || '',
      image_ids: app.image_ids || [],
      coordinator_email: app.coordinator_email || '',
    });
    setIsEditDialogOpen(true);
  };

  // Handle edit application
  const handleEditApplication = async (e) => {
    e.preventDefault();
    
    try {
      await applicationsAPI.update(selectedAppForAction.id, {
        profile_id: editAppData.profile_id === 'none' ? null : (editAppData.profile_id || null),
        notes: editAppData.notes || null,
        image_ids: editAppData.image_ids,
        coordinator_email: editAppData.coordinator_email || null,
      });
      toast.success('Application updated');
      setIsEditDialogOpen(false);
      setSelectedAppForAction(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    }
  };

  // Toggle image selection for edit
  const toggleEditImageSelection = (imageId) => {
    setEditAppData(prev => ({
      ...prev,
      image_ids: prev.image_ids.includes(imageId)
        ? prev.image_ids.filter(id => id !== imageId)
        : [...prev.image_ids, imageId]
    }));
  };

  // Toggle image selection
  const toggleImageSelection = (imageId) => {
    setNewAppData(prev => ({
      ...prev,
      image_ids: prev.image_ids.includes(imageId)
        ? prev.image_ids.filter(id => id !== imageId)
        : [...prev.image_ids, imageId]
    }));
  };

  const handleUpdateStatus = async (appId, newStatus) => {
    try {
      await applicationsAPI.update(appId, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteApplication = async (appId) => {
    if (!window.confirm('Delete this application and all its tasks?')) return;
    
    try {
      await applicationsAPI.delete(appId);
      toast.success('Application deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    if (!newTaskData.title) {
      toast.error('Task title is required');
      return;
    }

    try {
      await tasksAPI.create({
        application_id: selectedAppId,
        title: newTaskData.title,
        category: newTaskData.category,
        priority: newTaskData.priority,
        due_date: newTaskData.due_date || null,
        notes: newTaskData.notes || null,
      });
      toast.success('Task added');
      setIsNewTaskOpen(false);
      setNewTaskData({ title: '', category: 'other', priority: 'medium', due_date: '', notes: '' });
      fetchTasksForApp(selectedAppId);
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleToggleTask = async (taskId, appId, completed) => {
    try {
      await tasksAPI.update(taskId, { completed: !completed });
      fetchTasksForApp(appId);
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId, appId) => {
    try {
      await tasksAPI.delete(taskId);
      fetchTasksForApp(appId);
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  // Filter events that don't have applications yet
  const availableEvents = savedEvents.filter(
    event => !applications.some(app => app.event_id === event.id)
  );

  const filteredApplications = statusFilter === 'all' 
    ? applications 
    : applications.filter(app => app.status === statusFilter);

  const getTaskStats = (appId) => {
    const appTasks = tasks[appId] || [];
    const total = appTasks.length;
    const completed = appTasks.filter(t => t.completed).length;
    return { total, completed, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">Applications</h1>
              <p className="text-gray-600 mt-1">Track event applications and preparation tasks</p>
            </div>
            <HelpButton tutorialId="applications" />
          </div>
          <div className="flex gap-3">
            <Link to="/profiles">
              <Button variant="outline" className="border-2 border-[#1E3A5F]">
                <FileText className="w-4 h-4 mr-2" />
                Profiles
              </Button>
            </Link>
            <Button 
              onClick={() => setIsNewAppOpen(true)} 
              className="btn-primary"
              disabled={availableEvents.length === 0}
              data-testid="new-application-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Application
            </Button>
          </div>
        </div>

        {/* Status Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('all')}
            className={statusFilter === 'all' ? 'bg-[#1E3A5F]' : ''}
          >
            All ({applications.length})
          </Button>
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const count = applications.filter(a => a.status === status).length;
            return (
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={statusFilter === status ? 'bg-[#1E3A5F]' : ''}
              >
                {config.label} ({count})
              </Button>
            );
          })}
        </div>

        {/* Applications List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-12 text-center shadow-hard">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">
              {statusFilter === 'all' ? 'No applications yet' : `No ${STATUS_CONFIG[statusFilter]?.label.toLowerCase()} applications`}
            </h3>
            <p className="text-gray-600 mb-4">
              {availableEvents.length > 0 
                ? 'Start tracking your event applications'
                : 'Save some events first to create applications'}
            </p>
            {availableEvents.length > 0 && (
              <Button onClick={() => setIsNewAppOpen(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Create Application
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((app) => {
              const StatusIcon = STATUS_CONFIG[app.status]?.icon || FileText;
              const taskStats = getTaskStats(app.id);
              const isExpanded = expandedApp === app.id;
              
              return (
                <div
                  key={app.id}
                  className="bg-white rounded-xl border-2 border-[#1E3A5F] overflow-hidden shadow-hard"
                  data-testid={`application-${app.id}`}
                >
                  {/* Application Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-[#FFEDDF]/50 transition-colors"
                    onClick={() => toggleExpanded(app.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`p-2 rounded-lg ${STATUS_CONFIG[app.status]?.color || 'bg-gray-100'}`}>
                          <StatusIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[#1E3A5F] truncate">
                            {app.event?.title || 'Unknown Event'}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            {app.event?.date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(app.event.date)}
                              </span>
                            )}
                            {app.event?.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {app.event.city}, {app.event.state}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Status Badge */}
                        <Select
                          value={app.status}
                          onValueChange={(value) => {
                            handleUpdateStatus(app.id, value);
                          }}
                        >
                          <SelectTrigger 
                            className={`w-32 ${STATUS_CONFIG[app.status]?.color}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                              <SelectItem key={value} value={value}>
                                {config.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {/* Action Buttons */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(app);
                          }}
                          className="text-[#1E3A5F] hover:bg-[#FFEDDF]"
                          title="Edit application"
                          data-testid={`edit-btn-${app.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openExportDialog(app);
                          }}
                          className="text-[#1E3A5F] hover:bg-[#FFEDDF]"
                          title="Export (PDF, Copy, Print)"
                          data-testid={`export-btn-${app.id}`}
                        >
                          <FileDown className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCopyDialog(app);
                          }}
                          className="text-[#1E3A5F] hover:bg-[#FFEDDF]"
                          title="Copy to another event"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEmailDialog(app);
                          }}
                          className="text-[#1E3A5F] hover:bg-[#FFEDDF]"
                          title="Email to coordinator"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteApplication(app.id);
                          }}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Task Progress Bar - Always visible */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-3">
                        <ListTodo className="w-4 h-4 text-[#1E3A5F]" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-[#4A4A4A]">
                              Task Progress
                            </span>
                            <span className={`text-xs font-bold ${
                              taskStats.percentage === 100 ? 'text-green-600' : 
                              taskStats.percentage >= 50 ? 'text-blue-600' : 
                              'text-[#4A4A4A]'
                            }`}>
                              {taskStats.total > 0 ? `${taskStats.percentage}%` : 'No tasks'}
                            </span>
                          </div>
                          <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-300 rounded-full ${
                                taskStats.percentage === 100 ? 'bg-green-500' : 
                                taskStats.percentage >= 50 ? 'bg-blue-500' : 
                                taskStats.percentage > 0 ? 'bg-amber-500' : 
                                'bg-gray-300'
                              }`}
                              style={{ width: `${taskStats.total > 0 ? taskStats.percentage : 0}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {taskStats.completed}/{taskStats.total} done
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Content - Tasks & Images */}
                  {isExpanded && (
                    <div className="border-t-2 border-[#1E3A5F] bg-[#FFFFFF] p-4">
                      {/* Attached Images Section */}
                      {(app.images?.length > 0 || app.image_ids?.length > 0) && (
                        <div className="mb-6">
                          <h4 className="font-semibold text-[#1E3A5F] flex items-center gap-2 mb-3">
                            <ImageIcon className="w-4 h-4" />
                            Attached Images ({app.images?.length || app.image_ids?.length || 0})
                          </h4>
                          <div className="grid grid-cols-4 gap-3">
                            {app.images?.map((img) => (
                              <div key={img.id} className="relative group">
                                <img
                                  src={img.url}
                                  alt={img.name || 'Application image'}
                                  className="w-full h-24 object-cover rounded-lg border border-gray-200"
                                />
                                <p className="text-xs text-gray-500 mt-1 truncate">{img.name}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Tasks Section */}
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-[#1E3A5F] flex items-center gap-2">
                          <ListTodo className="w-4 h-4" />
                          Tasks ({taskStats.completed}/{taskStats.total} completed)
                        </h4>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedAppId(app.id);
                            setIsNewTaskOpen(true);
                          }}
                          data-testid={`add-task-btn-${app.id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Task
                        </Button>
                      </div>
                      
                      {tasks[app.id]?.length > 0 ? (
                        <div className="space-y-2">
                          {tasks[app.id].map((task) => (
                            <div
                              key={task.id}
                              className={`flex items-center gap-3 p-3 rounded-lg bg-white border ${
                                task.completed ? 'border-green-200 bg-green-50/50' : 'border-gray-200'
                              }`}
                            >
                              <Checkbox
                                checked={task.completed}
                                onCheckedChange={() => handleToggleTask(task.id, app.id, task.completed)}
                                data-testid={`task-checkbox-${task.id}`}
                              />
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium ${task.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                  {task.title}
                                </p>
                                <div className="flex items-center gap-3 text-xs">
                                  <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 capitalize">
                                    {task.category}
                                  </span>
                                  <span className={PRIORITY_CONFIG[task.priority]?.color}>
                                    {PRIORITY_CONFIG[task.priority]?.label}
                                  </span>
                                  {task.due_date && (
                                    <span className="text-gray-500">
                                      Due: {formatDate(task.due_date)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTask(task.id, app.id)}
                                className="text-gray-400 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-gray-500 py-4">
                          No tasks yet. Add tasks to track your preparation.
                        </p>
                      )}
                      
                      {/* Application Details */}
                      {(app.notes || app.confirmation_number || app.submission_date) && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            {app.submission_date && (
                              <div>
                                <span className="text-gray-500">Submitted:</span>{' '}
                                <span className="font-medium">{formatDate(app.submission_date)}</span>
                              </div>
                            )}
                            {app.confirmation_number && (
                              <div>
                                <span className="text-gray-500">Confirmation #:</span>{' '}
                                <span className="font-medium">{app.confirmation_number}</span>
                              </div>
                            )}
                            {app.notes && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Notes:</span>{' '}
                                <span>{app.notes}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* New Application Dialog */}
      <Dialog open={isNewAppOpen} onOpenChange={setIsNewAppOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F]">New Application</DialogTitle>
            <DialogDescription>
              Start tracking an application for one of your saved events
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateApplication} className="space-y-4">
            <div>
              <Label htmlFor="event">Select Event *</Label>
              <Select
                value={newAppData.event_id}
                onValueChange={(value) => setNewAppData({...newAppData, event_id: value})}
              >
                <SelectTrigger data-testid="select-event">
                  <SelectValue placeholder="Choose an event" />
                </SelectTrigger>
                <SelectContent>
                  {availableEvents.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.title} - {event.city}, {event.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="profile">Application Profile (Optional)</Label>
              <Select
                value={newAppData.profile_id}
                onValueChange={(value) => setNewAppData({...newAppData, profile_id: value})}
              >
                <SelectTrigger data-testid="select-profile">
                  <SelectValue placeholder="Select a profile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No profile</SelectItem>
                  {profiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.name} ({profile.business_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="template">Task Template (Optional)</Label>
              <Select
                value={newAppData.task_template_id}
                onValueChange={(value) => setNewAppData({...newAppData, task_template_id: value})}
              >
                <SelectTrigger data-testid="select-template">
                  <SelectValue placeholder="Select a task template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template (start fresh)</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} {template.is_system && '(System)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Templates pre-populate tasks to help you prepare
              </p>
            </div>
            
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newAppData.notes}
                onChange={(e) => setNewAppData({...newAppData, notes: e.target.value})}
                placeholder="Any notes about this application..."
                rows={2}
              />
            </div>
            
            {/* Image Selection */}
            <div>
              <Label className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Attach Images (Optional)
              </Label>
              {mediaItems.length > 0 ? (
                <div className="mt-2 grid grid-cols-4 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
                  {mediaItems.map((img) => (
                    <div 
                      key={img.id} 
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        newAppData.image_ids.includes(img.id) 
                          ? 'border-[#1E3A5F] ring-2 ring-[#1E3A5F]/30' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleImageSelection(img.id)}
                    >
                      <img
                        src={img.url}
                        alt={img.name}
                        className="w-full h-16 object-cover"
                      />
                      {newAppData.image_ids.includes(img.id) && (
                        <div className="absolute top-1 right-1 bg-[#1E3A5F] rounded-full p-0.5">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  No images in Media Library. <Link to="/media" className="text-[#1E3A5F] underline">Upload images</Link>
                </p>
              )}
              {newAppData.image_ids.length > 0 && (
                <p className="text-xs text-[#1E3A5F] mt-1 font-medium">
                  {newAppData.image_ids.length} image(s) selected
                </p>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsNewAppOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="create-application-btn">
                Create Application
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Task Dialog */}
      <Dialog open={isNewTaskOpen} onOpenChange={setIsNewTaskOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F]">Add Task</DialogTitle>
            <DialogDescription>
              Add a new task to this application
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div>
              <Label htmlFor="task-title">Task Title *</Label>
              <Input
                id="task-title"
                value={newTaskData.title}
                onChange={(e) => setNewTaskData({...newTaskData, title: e.target.value})}
                placeholder="What needs to be done?"
                required
                data-testid="task-title-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={newTaskData.category}
                  onValueChange={(value) => setNewTaskData({...newTaskData, category: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select
                  value={newTaskData.priority}
                  onValueChange={(value) => setNewTaskData({...newTaskData, priority: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([value, config]) => (
                      <SelectItem key={value} value={value}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="due-date">Due Date</Label>
              <Input
                id="due-date"
                type="date"
                value={newTaskData.due_date}
                onChange={(e) => setNewTaskData({...newTaskData, due_date: e.target.value})}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsNewTaskOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="create-task-btn">
                Add Task
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Copy Application Dialog */}
      <Dialog open={isCopyDialogOpen} onOpenChange={setIsCopyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F] flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Copy Application
            </DialogTitle>
            <DialogDescription>
              Copy this application to another event. Profile, notes, and images can be copied.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCopyApplication} className="space-y-4">
            {selectedAppForAction && (
              <div className="p-3 bg-[#FFEDDF] rounded-lg">
                <p className="text-sm font-medium text-[#1E3A5F]">
                  Copying from: {selectedAppForAction.event?.title}
                </p>
              </div>
            )}
            
            <div>
              <Label htmlFor="target-event">Copy to Event *</Label>
              <Select
                value={copyData.target_event_id}
                onValueChange={(value) => setCopyData({...copyData, target_event_id: value})}
              >
                <SelectTrigger data-testid="select-target-event">
                  <SelectValue placeholder="Select target event" />
                </SelectTrigger>
                <SelectContent>
                  {savedEvents
                    .filter(e => !applications.some(app => app.event_id === e.id))
                    .map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.title} - {event.city}, {event.state}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-3">
              <Label>What to copy:</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="copy-images"
                  checked={copyData.copy_images}
                  onCheckedChange={(checked) => setCopyData({...copyData, copy_images: checked})}
                />
                <label htmlFor="copy-images" className="text-sm">
                  Attached Images ({selectedAppForAction?.image_ids?.length || 0})
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="copy-notes"
                  checked={copyData.copy_notes}
                  onCheckedChange={(checked) => setCopyData({...copyData, copy_notes: checked})}
                />
                <label htmlFor="copy-notes" className="text-sm">Notes</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="copy-tasks"
                  checked={copyData.copy_tasks}
                  onCheckedChange={(checked) => setCopyData({...copyData, copy_tasks: checked})}
                />
                <label htmlFor="copy-tasks" className="text-sm">
                  Tasks (reset to incomplete)
                </label>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCopyDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="copy-application-btn">
                <Copy className="w-4 h-4 mr-2" />
                Copy Application
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Application Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F] flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Application
            </DialogTitle>
            <DialogDescription>
              Update your application details, profile, notes, and attached images.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditApplication} className="space-y-4">
            {selectedAppForAction && (
              <div className="p-3 bg-[#FFEDDF] rounded-lg">
                <p className="text-sm font-medium text-[#1E3A5F]">
                  {selectedAppForAction.event?.title}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {selectedAppForAction.event?.city}, {selectedAppForAction.event?.state} • {selectedAppForAction.event?.date ? formatDate(selectedAppForAction.event.date) : 'TBD'}
                </p>
              </div>
            )}
            
            {/* Profile Selection */}
            <div>
              <Label htmlFor="edit-profile">Artist Profile</Label>
              <Select
                value={editAppData.profile_id}
                onValueChange={(value) => setEditAppData({...editAppData, profile_id: value})}
              >
                <SelectTrigger data-testid="edit-select-profile">
                  <SelectValue placeholder="Select a profile (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Profile</SelectItem>
                  {profiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Profile includes your artist statement, product description, and booth info.
              </p>
            </div>
            
            {/* Coordinator Email */}
            <div>
              <Label htmlFor="edit-coordinator-email">Coordinator Email</Label>
              <Input
                id="edit-coordinator-email"
                type="email"
                value={editAppData.coordinator_email}
                onChange={(e) => setEditAppData({...editAppData, coordinator_email: e.target.value})}
                placeholder="coordinator@event.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Save the event coordinator's email for quick access when emailing your application.
              </p>
            </div>
            
            {/* Notes */}
            <div>
              <Label htmlFor="edit-notes">Application Notes</Label>
              <Textarea
                id="edit-notes"
                value={editAppData.notes}
                onChange={(e) => setEditAppData({...editAppData, notes: e.target.value})}
                placeholder="Add notes about this application, special requirements, or reminders..."
                rows={4}
              />
            </div>
            
            {/* Image Selection */}
            <div>
              <Label className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Attached Images
              </Label>
              {mediaItems.length > 0 ? (
                <div className="mt-2 grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                  {mediaItems.map((img) => (
                    <div 
                      key={img.id} 
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        editAppData.image_ids.includes(img.id) 
                          ? 'border-[#1E3A5F] ring-2 ring-[#1E3A5F]/30' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleEditImageSelection(img.id)}
                    >
                      <img
                        src={img.url}
                        alt={img.name}
                        className="w-full h-16 object-cover"
                      />
                      {editAppData.image_ids.includes(img.id) && (
                        <div className="absolute top-1 right-1 bg-[#1E3A5F] rounded-full p-0.5">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  No images in Media Library. <Link to="/media" className="text-[#1E3A5F] underline">Upload images</Link>
                </p>
              )}
              {editAppData.image_ids.length > 0 && (
                <p className="text-xs text-[#1E3A5F] mt-1 font-medium">
                  {editAppData.image_ids.length} image(s) selected
                </p>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-application-btn">
                <Check className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Application Dialog */}
      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F] flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Application
            </DialogTitle>
            <DialogDescription>
              Send this application to an event coordinator via email.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEmailApplication} className="space-y-4">
            {selectedAppForAction && (
              <div className="p-3 bg-[#FFEDDF] rounded-lg">
                <p className="text-sm font-medium text-[#1E3A5F]">
                  Application for: {selectedAppForAction.event?.title}
                </p>
                {selectedAppForAction.profile && (
                  <p className="text-xs text-gray-600 mt-1">
                    Using profile: {selectedAppForAction.profile.name}
                  </p>
                )}
              </div>
            )}
            
            <div>
              <Label htmlFor="recipient-email">Coordinator Email *</Label>
              <Input
                id="recipient-email"
                type="email"
                value={emailData.recipient_email}
                onChange={(e) => setEmailData({...emailData, recipient_email: e.target.value})}
                placeholder="coordinator@event.com"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="email-subject">Subject (Optional)</Label>
              <Input
                id="email-subject"
                type="text"
                value={emailData.subject}
                onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                placeholder="Auto-generated if left blank"
              />
            </div>
            
            <div>
              <Label htmlFor="email-message">Additional Message (Optional)</Label>
              <Textarea
                id="email-message"
                value={emailData.message}
                onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                placeholder="Add a personal message to the coordinator..."
                rows={3}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="include-images"
                checked={emailData.include_images}
                onCheckedChange={(checked) => setEmailData({...emailData, include_images: checked})}
              />
              <label htmlFor="include-images" className="text-sm">
                Include attached images ({selectedAppForAction?.image_ids?.length || 0})
              </label>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700">
                <strong>Email will include:</strong> Your profile info (artist statement, product description), 
                application notes, and selected images. The coordinator can reply directly to your email.
              </p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEmailDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="send-email-btn">
                <Send className="w-4 h-4 mr-2" />
                Send Email
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Export Application Dialog */}
      <ApplicationExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => {
          setIsExportDialogOpen(false);
          setSelectedAppForAction(null);
        }}
        application={selectedAppForAction}
        user={user}
      />
    </div>
  );
};

export default Applications;
